import React, { useMemo } from "react";
import { ExternalLink, Maximize2 } from "lucide-react";
import { cn } from "../../lib/utils";

/**
 * Checks if a string contains rich HTML chat embed components
 */
export function isRichEmbed(content) {
  if (!content || typeof content !== "string") return false;
  return (
    content.includes("ai-garment-carousel-wrapper") ||
    content.includes("ai-pricing-carousel") ||
    content.includes("ai-orders-container") ||
    content.includes("ai-order-card") ||
    content.includes("ai-msg-bubble-wrap") ||
    content.includes("ai-related-questions")
  );
}

/**
 * Checks if a string contains any HTML markup
 */
export function containsHtml(content) {
  if (!content || typeof content !== "string") return false;
  return /<\/?[a-z][\s\S]*>/i.test(content);
}

/**
 * Safely sanitizes HTML using the browser's native DOMParser.
 * Removes all executable tags, inline event handlers, and javascript: protocols.
 */
export function sanitizeChatHtml(rawHtml) {
  if (!rawHtml || typeof rawHtml !== "string") return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, "text/html");

    // Remove executable or form tags
    const dangerousTags = doc.querySelectorAll(
      "script, iframe, object, embed, form, input, textarea, base, link"
    );
    dangerousTags.forEach((el) => el.remove());

    // Clean dangerous attributes on all remaining elements
    const allElements = doc.querySelectorAll("*");
    allElements.forEach((el) => {
      const attrsToRemove = [];
      for (let i = 0; i < el.attributes.length; i++) {
        const attr = el.attributes[i];
        const name = attr.name.toLowerCase();
        const val = (attr.value || "").toLowerCase();

        if (name.startsWith("on") || val.includes("javascript:") || val.includes("data:text/html")) {
          attrsToRemove.push(attr.name);
        }
      }
      attrsToRemove.forEach((name) => el.removeAttribute(name));

      // Make links open securely in a new tab
      if (el.tagName.toLowerCase() === "a") {
        el.setAttribute("target", "_blank");
        el.setAttribute("rel", "noopener noreferrer");
      }
    });

    return doc.body.innerHTML;
  } catch (err) {
    console.warn("Error sanitizing chat HTML:", err);
    return rawHtml;
  }
}

/**
 * Enhanced Message Formatter for AI Chat & Conversations:
 * - Detects rich HTML widgets (Garment carousel, Pricing cards, Order cards)
 * - Safely sanitizes & renders them with interactive expand/collapse
 * - Preserves existing Markdown (**bold**, links, bullets, lightbox images) for standard text
 */
export default function FormattedMessage({ content, isUser, onImageClick }) {
  const hasHtml = useMemo(() => containsHtml(content), [content]);

  if (!content) return null;

  // Handle interactive clicks on the rendered HTML (Toggle expand/collapse & Image lightbox)
  const handleHtmlClick = (e) => {
    // 1. Handle "Show all items" / "Show less" garment carousel toggle
    const toggleBtn = e.target.closest(".ai-carousel-toggle-btn");
    if (toggleBtn) {
      e.preventDefault();
      e.stopPropagation();
      const wrapper = toggleBtn.closest(".ai-garment-carousel-wrapper");
      if (wrapper) {
        const moreContainer = wrapper.querySelector(".ai-garment-more-container");
        if (moreContainer) {
          const isHidden =
            moreContainer.style.display === "none" ||
            window.getComputedStyle(moreContainer).display === "none";

          moreContainer.style.display = isHidden ? "block" : "none";
          toggleBtn.setAttribute("aria-expanded", isHidden ? "true" : "false");
          toggleBtn.classList.toggle("is-expanded", isHidden);

          const span = toggleBtn.querySelector("span");
          if (span) {
            span.textContent = isHidden ? "Show less" : "Show all items";
          }
        }
      }
      return;
    }

    // 2. Handle image click inside carousel or card to open lightbox
    const img = e.target.closest("img");
    if (img && img.src && onImageClick) {
      e.preventDefault();
      e.stopPropagation();
      onImageClick(img.src);
    }
  };

  // If the message is raw HTML (from AI Check Price, Garment Carousel, Order Cards, etc.)
  if (hasHtml && !isUser) {
    const sanitized = sanitizeChatHtml(content);

    return (
      <div
        className="ai-rich-message-container w-full"
        onClick={handleHtmlClick}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }

  // Otherwise, render using standard text & markdown formatter
  const lines = content.split("\n");

  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        if (!line.trim()) {
          return <div key={i} className="h-1.5" />;
        }

        // Tokenize line for **bold** and [label](url)
        const regex = /(\*\*.*?\*\*|\[.*?\]\(https?:\/\/[^\s\)]+\))/g;
        const parts = [];
        let cursor = 0;
        let match;

        while ((match = regex.exec(line)) !== null) {
          if (match.index > cursor) {
            parts.push(line.slice(cursor, match.index));
          }
          const token = match[0];
          if (token.startsWith("**") && token.endsWith("**")) {
            parts.push(
              <strong key={`${i}-${match.index}`} className={cn("font-bold", !isUser && "text-slate-900")}>
                {token.slice(2, -2)}
              </strong>
            );
          } else if (token.startsWith("[") && token.includes("](") && token.endsWith(")")) {
            const linkText = token.slice(1, token.indexOf("]("));
            const linkUrl = token.slice(token.indexOf("](") + 2, -1);
            const isImg = /\.(png|jpe?g|webp|gif)(\?.*)?$/i.test(linkUrl) || linkUrl.includes("/storage/");

            if (isImg) {
              parts.push(
                <span key={`img-${i}-${match.index}`} className="inline-flex flex-col my-1.5 align-middle block">
                  <span
                    onClick={() => onImageClick && onImageClick(linkUrl)}
                    className="cursor-pointer group relative inline-block rounded-lg border border-slate-200 overflow-hidden shadow-xs bg-slate-100 max-w-xs"
                    title="Click to enlarge"
                  >
                    <img
                      src={linkUrl}
                      alt={linkText}
                      referrerPolicy="no-referrer"
                      className="max-h-36 rounded-lg object-cover transition-transform duration-200 group-hover:scale-105"
                      loading="lazy"
                    />
                    <span className="absolute inset-0 bg-black/25 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-[11px] font-semibold gap-1 transition-opacity">
                      <Maximize2 className="w-3.5 h-3.5" />
                      View Image
                    </span>
                  </span>
                  <a
                    href={linkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] underline text-sky-600 hover:text-sky-800 flex items-center gap-1 mt-0.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3" />
                    {linkText}
                  </a>
                </span>
              );
            } else {
              parts.push(
                <a
                  key={`link-${i}-${match.index}`}
                  href={linkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-sky-600 hover:text-sky-800 font-medium"
                  onClick={(e) => e.stopPropagation()}
                >
                  {linkText}
                </a>
              );
            }
          }
          cursor = match.index + match[0].length;
        }

        if (cursor < line.length) {
          parts.push(line.slice(cursor));
        }

        const isBullet = line.trim().startsWith("•") || line.trim().startsWith("-") || line.trim().startsWith("*");

        return (
          <div key={i} className={cn(isBullet && "pl-2")}>
            {parts}
          </div>
        );
      })}
    </div>
  );
}
